// Internal modules
import { appStore, dispatchFormAction } from '@/store';
import { logProcessBillingPageToFpti } from './fpti';
import {
  approvePayment,
  complete3DS,
  createOnboardingPaymentPlan,
  onboardAction,
  onboardErrorHandler,
} from './graphql';
import {
  filterFormState,
  getFieldData,
  logBlurOrFocusEvent,
  shouldDisplayErrorOnChange,
  validateForm,
} from './utils';
import {
  $logger,
  features,
  focusOnFirstError,
  getFieldError,
  handleApiError,
  INPUT_FIELD_ERROR_STATUS_PAYLOAD,
  parseGqlError,
} from '@/lib';

/**
 * @TECH_DEBT This function used to have more logic but it has been
 * removed. When we refactor the state management to use Apollo Client
 * we should go through all the places where this function, `blurHandler`
 * is used and swap it to reference `logBlurOrFocusEvent`
 */
const blurHandler = logBlurOrFocusEvent;

const changeHandler = (e, value, oldValue, meta = {}) => {
  e.persist();

  // Using id instead of name attribute for consistency in state names since
  // some name attributes have dashes to be compatible with autocomplete
  const { id, checked: isChecked, type: fieldType } = e.target;
  const { addressType, currentState, field } = getFieldData(e.target, meta);
  const { errors = [], isValid } = meta.validation || {};
  const errorCode = errors[0] || '';
  const isCheckbox = fieldType === 'checkbox';

  /**
   * Display the error if we are currently displaying the error and the
   * field is invalid. We check the `displayError` property because
   * we don't want to show the error when the user first starts filling
   * out the field. For example, when the user enters the first character
   * into the email field it will be invalid because it doesn't match
   * the pattern for an email but immediately showing the error will
   * confuse the user.
   */
  const shouldDisplayError = shouldDisplayErrorOnChange(id, currentState, meta);
  const payload = {
    displayError: shouldDisplayError,
    error: getFieldError({ errorCode, field, isValid, value }),
    errorCode,
    isDirty: true,
    isValid,
    value: isCheckbox ? isChecked : value,
  };

  // Log the input error if we are displaying or removing the error
  // or if the error is displayed and the error code changes
  const isErrorStatusChange = shouldDisplayError !== currentState.displayError;
  const isErrorCodeChange = errorCode !== currentState.errorCode;
  if (isErrorStatusChange || (isErrorCodeChange && currentState.displayError)) {
    const logPayload = INPUT_FIELD_ERROR_STATUS_PAYLOAD(id, isValid, errorCode);
    $logger.fpti(`${isValid ? 'clear' : 'set'}_input_error`, logPayload);
  }

  // Do not set cardType if CardTypeSelector component is enabled
  // Reason: cardNumber.cardType state will be updated by CardTypeSelector
  const { ui: uiState } = appStore.getState();
  if (field === 'cardNumber' && !uiState.hasCardTypeSelector) {
    payload.cardType = meta.cardType;
  }

  dispatchFormAction({ addressType, field, payload });
};

const threeDS1ContingencyHandler = threeDS1ContingencyData => {
  const { api: apiActions, ui: uiActions } = appStore.getActions();

  // Add event listener to window to confirm 3ds
  window.__complete3DS__ = token => {
    return complete3DS(token).catch(err => {
      const { error, message } = parseGqlError(err);
      $logger.error('weasley_complete_3ds_error', {
        error: message,
      });

      uiActions.toggleIsFormSubmitting(false);
      uiActions.toggleIsThreeDS1Shown(false);
      handleApiError(error);
    });
  };

  apiActions.setThreeDS1ContingencyData(threeDS1ContingencyData);
  uiActions.toggleIsFormSubmitting(false);
  uiActions.toggleIsThreeDS1Shown(true);
};

const threeDS2ContingencyHandler = threeDS2ContingencyData => {
  const { api: apiActions, ui: uiActions } = appStore.getActions();

  apiActions.setThreeDS2ContingencyData(threeDS2ContingencyData);
  uiActions.toggleIsFormSubmitting(false);
};

const threeDSHandler = ({
  is3DSecureRequired,
  threeDS2ContingencyData,
  threeDS1ContingencyData,
}) => {
  // If 3DS is not required we are done and can approve
  // the payment and return the user to the merchant
  if (!is3DSecureRequired) {
    return approvePayment();
  }

  if (threeDS1ContingencyData) {
    threeDS1ContingencyHandler(threeDS1ContingencyData);
  } else {
    threeDS2ContingencyHandler(threeDS2ContingencyData);
  }
};

const displayFormErrors = (fieldsWithErrors, formErrors) => {
  const { form: formActions } = appStore.getActions();
  const addressRegex = /^(billing|shipping)Address$/;
  fieldsWithErrors.forEach(([field, payload]) => {
    const isAddressField = addressRegex.test(field);
    const fieldName = isAddressField ? 'addressType' : 'field';
    const action = isAddressField
      ? 'updateAddressField'
      : 'updateNonAddressField';

    formActions[action]({
      [fieldName]: field,
      payload,
      ...(isAddressField && { isBatchUpdate: true }),
    });
  });

  focusOnFirstError(formErrors);
};

const submitHandler = e => {
  e.preventDefault();
  const { form: formState, ui: uiState } = appStore.getState();

  // Disable form interaction during submission
  if (uiState.isFormSubmitting) {
    return;
  }

  // prevent form submission and scroll to error when shipping region is not
  // supported
  if (uiState.pageLevelError === 'pageLevelError.shippingRegionUnsupported') {
    window.scrollTo(0, 0);
    return;
  }

  const { ui: uiActions } = appStore.getActions();
  const filteredFormState = filterFormState(formState);
  const { errorLogPayload, formErrors } = validateForm(filteredFormState);
  const fieldsWithErrors = Object.entries(formErrors);

  // If form is not valid, add errors to state and display them
  if (fieldsWithErrors.length) {
    $logger.fpti('invalid_form_submission', {
      state_name: 'billing',
      transition_name: 'process_invalid_billing_page_submission',
    });
    displayFormErrors(fieldsWithErrors, formErrors);
    $logger.info('weasley_invalid_form_submission', errorLogPayload);
    return $logger.flush();
  }

  // Clear any existing page level error and show form submitting spinner
  uiActions.toggleIsFormSubmitting(true);
  uiActions.setPageLevelError('');

  logProcessBillingPageToFpti();

  const hasAddressNormalization = features.isActive('hasAddressNormalization');
  const isAllowChangeShipping = features.isActive('isAllowChangeShipping');
  const { bypassAddressNormalization } = formState;

  // Check to see if normalization flow should be triggered
  if (hasAddressNormalization) {
    if (isAllowChangeShipping && !bypassAddressNormalization) {
      $logger.info('weasley_normalize_address');

      return import(
        '@/components/Interstitials/AddressNormalizationInterstitial/api'
      )
        .then(addressNormalizationApi => {
          return addressNormalizationApi
            .isAddressNormalized()
            .then(shouldCallOnboardAccount => {
              $logger.info('weasley_normalize_address_final_response', {
                shouldCallOnboardAccount,
              });
              return shouldCallOnboardAccount && onboardAccount();
            });
        })
        .catch(e => {
          // continue with out address normalization interstitial experience
          // if api file fails to load.
          $logger.error('weasley_error_loading_normalize_address_api_file', e);
        });
    }

    $logger.info('weasley_skip_address_normalization', {
      bypassAddressNormalization,
      hasAddressNormalization,
      isAllowChangeShipping,
    });
  }

  if (features.isActive('shouldShowSCAPhoneConfirmation')) {
    const { value: onboardOption } = formState.onboardOption;
    const isSignup = onboardOption === 'signup';

    if (isSignup && !formState.phone.meta?.isConfirmed) {
      uiActions.toggleIsFormSubmitting(false);
      uiActions.toggleIsPhoneConfirmationOpen(true);
      return Promise.resolve();
    }
  }

  // Returning the Promise so we can await it in tests
  return onboardAccount(filteredFormState);
};

const onboardAccount = filteredFormState => {
  // In address normalization flow filteredFormState would not be passed as the
  // form state could change during the normalization flow.
  if (!filteredFormState) {
    const { form: formState } = appStore.getState();
    filteredFormState = filterFormState(formState);
  }

  // Returning the Promise so we can await it in tests
  return onboardAction({ ...filteredFormState })
    .then(threeDSHandler)
    .catch(onboardErrorHandler);
};

const onboardingPaymentPlanHandler = () => {
  const { form: formState } = appStore.getState();
  const filteredFormState = filterFormState(formState);

  return createOnboardingPaymentPlan(filteredFormState)
    .then(threeDSHandler)
    .catch(handleApiError);
};

export {
  blurHandler,
  changeHandler,
  displayFormErrors,
  onboardAccount,
  onboardingPaymentPlanHandler,
  submitHandler,
  threeDSHandler,
};
